import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * UI language for the unified staff portal (ADR-012). Independent of the
 * customer's language (the bot already matches the customer; this is what the
 * human staff member reads). Ported from agent-console's langStore as part of
 * the Phase-6 i18n unification — admin-portal previously had no i18n at all.
 *
 * The root `dir`/`lang` attributes and every `useT()` consumer subscribe to
 * this single source of truth.
 */
export type Lang = 'en' | 'he';

/**
 * Default UI language for the unified staff portal. Decision (ADR-012, Phase-6
 * clarification): English default + toggle, RTL fully preserved when `he` is
 * chosen. A per-user choice is persisted to localStorage and overrides this on
 * subsequent loads. One constant so a different default per build is a one-line
 * change.
 */
export const DEFAULT_LANG: Lang = 'en';

interface LangState {
  lang: Lang;
  setLang: (lang: Lang) => void;
  toggle: () => void;
}

export const useLangStore = create<LangState>()(
  persist(
    (set, get) => ({
      lang: DEFAULT_LANG,
      setLang: (lang) => set({ lang }),
      toggle: () => set({ lang: get().lang === 'he' ? 'en' : 'he' }),
    }),
    // Namespaced `css-platform-*` key so the unified portal doesn't collide with
    // agent-console's `css-agent-lang` while both apps still exist (agent-console
    // retires in 6.7).
    { name: 'css-platform-lang' }
  )
);

/** `'rtl'` for Hebrew, `'ltr'` otherwise — used for the document `dir`. */
export const dirFor = (lang: Lang): 'rtl' | 'ltr' => (lang === 'he' ? 'rtl' : 'ltr');
